import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Plus, Trash2, ShieldCheck, Mail, UserCheck, KeyRound } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { LoadingSpinner } from '../Layout';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ageOf = (dob) => (dob ? Math.floor((Date.now() - new Date(dob).getTime()) / 31557600000) : null);

/**
 * The one gate for adding a child: who they are, then a signed consent, then they exist.
 * Used inside the welcome AND on the family page afterwards, same rules either way.
 */
export const AddChildForm = ({ accent = '#22d3ee', onAdded, compact = false }) => {
  const { getAuthHeader } = useAuth();
  const [f, setF] = useState({ first_name: '', last_name: '', date_of_birth: '', email: '', grade_level: '' });
  const [loginSetup, setLoginSetup] = useState('parent_set');
  const [password, setPassword] = useState('');
  const [signed, setSigned] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [consent, setConsent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/family/consent-text`).then(r => setConsent(r.data)).catch(() => {});
  }, []);

  const age = ageOf(f.date_of_birth);
  const needsEmail = age !== null && age >= 13 && age < 18;
  const managed = age !== null && age < 13;
  const parentSet = needsEmail && loginSetup === 'parent_set';

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...f, signed_name: signed, agreed, login_setup: loginSetup };
      if (!payload.email) delete payload.email;              // empty string is not an address
      if (!payload.grade_level) delete payload.grade_level;
      if (parentSet) payload.password = password;
      const r = await axios.post(`${API}/family/children`, payload, getAuthHeader());
      toast.success(r.data.message || `${r.data.child.name} is set up`);
      setF({ first_name: '', last_name: '', date_of_birth: '', email: '', grade_level: '' });
      setSigned(''); setAgreed(false); setPassword('');
      onAdded?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add them just now');
    } finally {
      setSaving(false);
    }
  };

  const ready = f.first_name.trim() && f.date_of_birth && agreed && signed.trim().length >= 3
    && (!needsEmail || f.email.includes('@')) && (!parentSet || password.length >= 8);

  return (
    <div className="space-y-5" data-testid="add-child-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-slate-300 text-sm">Child's first name</Label>
          <Input value={f.first_name} onChange={e => setF({ ...f, first_name: e.target.value })}
            className="input-dark" data-testid="child-first-name" />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300 text-sm">Last name</Label>
          <Input value={f.last_name} onChange={e => setF({ ...f, last_name: e.target.value })}
            className="input-dark" data-testid="child-last-name" />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300 text-sm">Date of birth</Label>
          <Input type="date" value={f.date_of_birth}
            onChange={e => setF({ ...f, date_of_birth: e.target.value })}
            className="input-dark" data-testid="child-dob" />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300 text-sm">Grade (optional)</Label>
          <Input value={f.grade_level} onChange={e => setF({ ...f, grade_level: e.target.value })}
            placeholder="e.g. 9th" className="input-dark" data-testid="child-grade" />
        </div>
      </div>

      <AnimatePresence mode="wait">
        {age !== null && age >= 0 && (
          <motion.div key={managed ? 'managed' : 'own'} initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="rounded-xl border border-white/10 bg-slate-900/50 p-4" data-testid="child-access-note">
            {managed ? (
              <p className="text-sm text-slate-300 flex items-start gap-2">
                <UserCheck className="w-4 h-4 mt-0.5 shrink-0" style={{ color: accent }} />
                <span>At {age}, there's no login, <span className="text-white">you work on their behalf</span>,
                  and everything stays under your account. That's how we keep it right for under-13s.</span>
              </p>
            ) : (
              <p className="text-sm text-slate-300 flex items-start gap-2">
                <Mail className="w-4 h-4 mt-0.5 shrink-0" style={{ color: accent }} />
                <span>At {age}, the answers have to be theirs, so they get their own login. You can
                  set it up for them right now and hand it over, or email them an invitation.</span>
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {needsEmail && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">The email they'll sign in with</Label>
            <Input type="email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })}
              className="input-dark" data-testid="child-email" />
          </div>

          <div className="grid sm:grid-cols-2 gap-3" data-testid="login-setup-choice">
            {[['parent_set', "I'll set it up now",
               'You choose their first password. Nothing is emailed. You can set their FuturePath up straight away.'],
              ['invite', 'Email them an invitation',
               'They get a link to create their own password. Their profile waits until they do.']]
              .map(([key, title, line]) => (
                <button key={key} type="button" onClick={() => setLoginSetup(key)}
                  data-testid={`login-setup-${key}`}
                  className={`text-left rounded-xl border p-4 transition-colors ${loginSetup === key
                    ? 'border-white/40 bg-white/[0.06]' : 'border-white/10 hover:border-white/25'}`}>
                  <p className="text-sm text-white font-medium flex items-center gap-2">
                    {key === 'parent_set' ? <KeyRound className="w-3.5 h-3.5" style={{ color: accent }} />
                      : <Mail className="w-3.5 h-3.5" style={{ color: accent }} />}
                    {title}
                  </p>
                  <p className="text-xs text-slate-300 mt-1.5 leading-relaxed">{line}</p>
                </button>
              ))}
          </div>

          {parentSet && (
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Their first password</Label>
              <Input type="password" value={password} onChange={e => setPassword(e.target.value)}
                placeholder="At least 8 characters" className="input-dark" data-testid="child-password" />
              <p className="text-xs text-slate-300">
                Share this with {f.first_name || 'them'}. The first time they sign in, FuturePath asks
                them to choose their own password, and from that moment the profile is theirs and
                you switch to viewing it.
              </p>
            </div>
          )}
        </div>
      )}

      <div className="rounded-xl border p-4" style={{ borderColor: `${accent}33`, background: `${accent}0d` }}>
        <p className="text-xs uppercase tracking-[0.16em] flex items-center gap-1.5" style={{ color: accent }}>
          <ShieldCheck className="w-3.5 h-3.5" /> Parental consent
        </p>
        <p className="text-sm text-slate-300 mt-3 leading-relaxed" data-testid="consent-text">
          {consent?.text || 'Loading…'}
        </p>
        <label className="flex items-start gap-3 mt-4 cursor-pointer">
          <Checkbox checked={agreed} onCheckedChange={v => setAgreed(!!v)} data-testid="consent-agree" />
          <span className="text-sm text-slate-300">I have read and agree to the above.</span>
        </label>
        <div className="mt-4 grid sm:grid-cols-2 gap-4 items-end">
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">Type your full name to sign</Label>
            <Input value={signed} onChange={e => setSigned(e.target.value)} placeholder="Your full legal name"
              className="input-dark" style={{ fontFamily: 'Outfit, serif' }} data-testid="consent-signature" />
          </div>
          <p className="text-xs text-slate-300">
            Signed {new Date().toLocaleDateString()} · we record the date and your IP address with it.
          </p>
        </div>
      </div>

      <Button onClick={submit} disabled={!ready || saving} data-testid="add-child-submit"
        className={`rounded-full text-slate-900 font-semibold ${compact ? '' : 'w-full py-6'}`}
        style={{ background: `linear-gradient(90deg, ${accent}, #a78bfa)` }}>
        {saving ? <LoadingSpinner size="sm" /> : <><Plus className="w-4 h-4 mr-1.5" /> Add this child</>}
      </Button>
    </div>
  );
};

/** The welcome step: add one or more children, then see what the family actually needs. */
export const AddChildrenStep = ({ accent = '#22d3ee', onDone }) => {
  const { getAuthHeader } = useAuth();
  const [added, setAdded] = useState([]);
  const [fit, setFit] = useState(null);
  const [showForm, setShowForm] = useState(true);

  useEffect(() => {
    axios.get(`${API}/family/plan-fit`, getAuthHeader()).then(r => setFit(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl mx-auto" data-testid="add-children-step">
      <p className="text-[10px] uppercase tracking-[0.22em] text-slate-300 text-center">Your family</p>
      <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white text-center tracking-tight mt-3">
        Who are you planning for?
      </h2>
      <p className="text-sm text-slate-300 text-center mt-3 max-w-lg mx-auto">
        Add each child now and sign their consent once. You can add another whenever you need to,         it just runs the same two steps.
      </p>

      {added.length > 0 && (
        <div className="mt-7 space-y-2" data-testid="added-children">
          {added.map((c) => (
            <motion.div key={c.child.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-3 rounded-xl border border-emerald-400/25 bg-emerald-500/[0.07] px-4 py-3">
              <ShieldCheck className="w-4 h-4 text-emerald-300 shrink-0" />
              <span className="text-sm text-white font-medium">{c.child.name}</span>
              <span className="text-xs text-slate-300">{c.child.age}</span>
              <span className="text-xs text-slate-300 ml-auto">
                {c.child.managed ? 'You manage this profile'
                  : c.child.login_ready ? 'You set their password · you can set them up now'
                    : 'Invitation sent'} · consent signed
              </span>
            </motion.div>
          ))}
        </div>
      )}

      {fit && added.length > 0 && !fit.already_enough && (
        <div className="mt-6 rounded-xl border border-amber-400/30 bg-amber-500/[0.08] p-4" data-testid="plan-fit-nudge">
          <p className="text-sm text-white font-medium">{fit.headline}</p>
          <p className="text-xs text-slate-300/90 mt-2">
            Highly recommended: <span className="text-white font-semibold">{fit.recommended_plan_name} at
              ${fit.recommended_price}/mo</span>, it's built to be shared, and it's the one most families
            with {fit.people} people stay on. {fit.note}
          </p>
        </div>
      )}

      <div className="mt-7">
        {showForm ? (
          <AddChildForm accent={accent} onAdded={(r) => {
            setAdded((a) => [...a, r]); setFit(r.plan_fit); setShowForm(false);
          }} />
        ) : (
          <Button variant="outline" onClick={() => setShowForm(true)} data-testid="add-another-child"
            className="w-full rounded-full border-slate-700 text-slate-200 py-6">
            <Plus className="w-4 h-4 mr-1.5" /> Add another child
          </Button>
        )}
      </div>

      <Button onClick={() => onDone(added)} data-testid="children-continue"
        disabled={added.length === 0}
        className="w-full mt-4 rounded-full text-slate-900 font-semibold py-6"
        style={{ background: `linear-gradient(90deg, ${accent}, #a78bfa)` }}>
        {added.length ? <>Continue <ArrowRight className="w-4 h-4 ml-1.5" /></> : 'Add a child to continue'}
      </Button>
      <button onClick={() => onDone([])} data-testid="children-skip"
        className="w-full text-xs text-slate-300 hover:text-slate-300 mt-4 transition-colors">
        I'll add my children later
      </button>
    </motion.div>
  );
};

export default AddChildrenStep;
